<template>
  <div class="images" :class="[`images-${aspectRatioClass}`, { 'is--confined': confine }]">
    <DynamicImage
      v-if="computedImageData.image"
      :image="computedImageData.image"
      :show-source="true"
      :renditions="computedRenditions"
      :loading="loading ? loading : computedImageData._index === 0 ? 'eager' : 'lazy'"
      :priority="priority ? priority : computedImageData._index === 0 ? 'auto' : 'low'"
    />
    <Caption
      v-if="computedImageData.showCaption && computedImageData.caption"
      :content="computedImageData.caption"
      :max-lines="3"
      class="image-caption"
      :has-padding="captionHasPadding"
    />
  </div>
</template>

<script setup lang="ts">
import type { ICmsContentImages } from '~~/types/_cms-types';
import DynamicImage from '~/components/DynamicImage.vue';
import type { IImageLoadingSettings, IImageRendition, TAspectRatio } from '~~/types/local_types';

interface IProps extends ICmsContentImages, IImageLoadingSettings {
  _index: number;
  showCaption?: boolean;
  confine?: boolean;
  captionHasPadding?: boolean;
  staticData?: never;
}

const props = withDefaults(defineProps<IProps>(), {
  showCaption: true,
  captionHasPadding: false,
  confine: true,
  staticData: undefined,
});

/*
  Performance HTTP1.1: when this component is directly used in a page it's NOT dynamically populated with data
  in that case the data is part of staticData.
 */
const computedImageData = computed(() => {
  if (props.staticData?.item) {
    return { ...props, ...props.staticData.item };
  } else {
    return props;
  }
});

const computedRenditions = computed(() => {
  const renditions: IImageRendition[] = [
    {
      breakpoint: 'sm',
      size: 480,
      aspectRatio: (computedImageData.value.aspectRatio || '16x9') as TAspectRatio,
    },
    {
      breakpoint: 'md',
      size: 800,
      aspectRatio: (computedImageData.value.aspectRatio || '16x9') as TAspectRatio,
    },
    {
      breakpoint: 'lg',
      size: 1024,
      aspectRatio: (computedImageData.value.aspectRatio || '16x9') as TAspectRatio,
    },
  ];

  return renditions;
});

const aspectRatioClass = computed(() => {
  return (computedImageData.value.aspectRatio || '16/9').split(/[/:]/).join('x');
});
</script>

<style scoped lang="postcss">
.images {
  &.is--confined {
    &.images-16x9,
    &.images-4x3,
    &.images-1x1 {
      width: 100%;
    }

    &.images-3x4 {
      width: 75%;
      margin: 0 12.5%;
    }
    &.images-9x16 {
      width: 56.25%;
      margin: 0 21.875%;
    }
  }
}
</style>
